import React, { useState } from "react";
import {
  ActionIcon,
  Box,
  Button,
  Group,
  Menu,
  Modal,
  Stack,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import {
  IconPencil,
  IconPlus,
  IconCreditCard,
  IconStar,
} from "@tabler/icons-react";
import { ClientPaymentMethod, isCardExpired } from "../../../types/cardOnFile";
import { useApp } from "../../../context/AppContext";
import RadioRingRow from "./RadioRingRow";
import EmptyStateCard from "../../common/surface/EmptyStateCard";
import styles from "./CheckoutPanel.module.css";

interface CardOnFileRevealProps {
  cards: ClientPaymentMethod[];
  selectedCardId: number | null;
  onSelectCard: (id: number) => void;
  /** Opens the shared AddCardModal (rendered by CheckoutPanel). */
  onAddCard: () => void;
  /** Refetch saved cards after nickname/default edits. */
  onCardsChanged: () => Promise<void> | void;
  clientId: string;
}

function brandBadge(card: ClientPaymentMethod) {
  const brand = (card.cardBrand || "").toLowerCase();
  if (brand === "visa") {
    return (
      <Box className={`${styles.brandBadge} ${styles.brandVisa}`}>VISA</Box>
    );
  }
  if (brand === "mastercard") {
    return (
      <Box className={`${styles.brandBadge} ${styles.brandMastercard}`}>MC</Box>
    );
  }
  if (brand === "amex" || brand === "american express") {
    return (
      <Box className={`${styles.brandBadge} ${styles.brandAmex}`}>AMEX</Box>
    );
  }
  return (
    <Box className={`${styles.brandBadge} ${styles.brandGeneric}`}>
      {(card.cardBrand || "Card").slice(0, 4)}
    </Box>
  );
}

function displayBrand(card: ClientPaymentMethod): string {
  const brand = card.cardBrand || "Card";
  return brand.charAt(0).toUpperCase() + brand.slice(1);
}

/**
 * Card on File reveal: saved cards as radio ring rows (brand badge, last4,
 * expiry), per-card edit menu (nickname / set default via existing AppContext
 * methods), an inline "Add a new card" link, and a dashed empty state.
 */
export default function CardOnFileReveal({
  cards,
  selectedCardId,
  onSelectCard,
  onAddCard,
  onCardsChanged,
  clientId,
}: CardOnFileRevealProps) {
  const { setClientPaymentMethodNickname, setDefaultClientPaymentMethod } =
    useApp();
  const [nicknameCard, setNicknameCard] = useState<ClientPaymentMethod | null>(
    null,
  );
  const [nicknameDraft, setNicknameDraft] = useState("");
  const [isSavingNickname, setIsSavingNickname] = useState(false);
  const [isSettingDefault, setIsSettingDefault] = useState(false);

  if (cards.length === 0) {
    return (
      <EmptyStateCard
        icon={<IconCreditCard size={20} stroke={1.9} />}
        title="No cards on file"
        message="This client hasn't saved a card yet. Add one to charge it directly."
        actionLabel="Add card"
        onAction={onAddCard}
      />
    );
  }

  const handleSetDefault = async (card: ClientPaymentMethod) => {
    try {
      setIsSettingDefault(true);
      await setDefaultClientPaymentMethod(clientId, card.id);
      await onCardsChanged();
    } catch (err) {
      console.error("Failed to set default card:", err);
    } finally {
      setIsSettingDefault(false);
    }
  };

  const handleSaveNickname = async () => {
    if (!nicknameCard) return;
    try {
      setIsSavingNickname(true);
      await setClientPaymentMethodNickname(
        clientId,
        nicknameCard.id,
        nicknameDraft.trim(),
      );
      await onCardsChanged();
      setNicknameCard(null);
    } catch (err) {
      console.error("Failed to save card nickname:", err);
    } finally {
      setIsSavingNickname(false);
    }
  };

  return (
    <Box className={styles.reveal}>
      {cards.map((card) => {
        const expired = isCardExpired(card);
        return (
          <RadioRingRow
            key={card.id}
            selected={selectedCardId === card.id}
            onSelect={() => onSelectCard(card.id)}
            disabled={expired}
            leading={brandBadge(card)}
            title={
              <>
                {card.nickname
                  ? card.nickname
                  : `${displayBrand(card)} •••• ${card.cardLast4}`}
                {card.isDefault && (
                  <Box component="span" className={styles.lastUsedChip}>
                    Default
                  </Box>
                )}
              </>
            }
            caption={
              expired
                ? `Expired ${card.cardExpMonth}/${card.cardExpYear}`
                : `Expires ${card.cardExpMonth}/${card.cardExpYear}`
            }
            rightSection={
              <Menu position="bottom-end" withinPortal shadow="md">
                <Menu.Target>
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    aria-label={`Edit card ending in ${card.cardLast4}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <IconPencil size={15} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    leftSection={<IconPencil size={14} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      setNicknameCard(card);
                      setNicknameDraft(card.nickname || "");
                    }}
                  >
                    Edit nickname
                  </Menu.Item>
                  {!card.isDefault && (
                    <Menu.Item
                      leftSection={<IconStar size={14} />}
                      disabled={isSettingDefault}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSetDefault(card);
                      }}
                    >
                      Set as default
                    </Menu.Item>
                  )}
                </Menu.Dropdown>
              </Menu>
            }
          />
        );
      })}

      <UnstyledButton className={styles.inlineLink} onClick={onAddCard}>
        <IconPlus size={15} />
        Add a new card
      </UnstyledButton>

      {/* Nickname editor */}
      <Modal
        opened={nicknameCard !== null}
        onClose={() => setNicknameCard(null)}
        title="Edit card nickname"
        size="sm"
        centered
      >
        <Stack gap="md">
          <TextInput
            label="Nickname"
            placeholder="e.g. Personal card"
            value={nicknameDraft}
            onChange={(e) => setNicknameDraft(e.currentTarget.value)}
            maxLength={40}
          />
          <Group justify="flex-end" gap="sm">
            <Button
              variant="light"
              color="gray"
              onClick={() => setNicknameCard(null)}
              disabled={isSavingNickname}
            >
              Cancel
            </Button>
            <Button
              color="var(--brand-teal)"
              onClick={handleSaveNickname}
              loading={isSavingNickname}
            >
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
}
